import type { Measurement, MeasurementType } from "./health";
import {
  WELLBEING_SERIES_QUESTIONS,
  type WellbeingSeriesQuestionId,
  type WellbeingSeriesView,
} from "./health-over-time";
import { registerQuestion } from "./question-register.v1";

// TES-1482 (TES-1458 §5, Ö-715) — which of her graphs the coach may read.
// Emelie 2026-10-20:
// "Coachens åtkomst följer uttryckliga behörigheter; önskemålet om gemensamma grafer ger inte automatiskt tillgång till alla medicinska uppgifter."
//
// The explicit permission is the question register's `visibility.coach`
// (question-register.v1.ts), and nothing else: a series reaches the coach
// only when the register question it measures says so. Today that is
// WELLBEING_7D and WEIGHT_KG, the two A-01 names for her:
// "Coachen ska se 8–12 veckors separata trendgrafer för övergripande mående 1–5 och frivilligt registrerad vikt. Graferna får inte kombineras till en score eller medicinsk slutsats."
//
// Appetite (MONTH_HUNGER/SATIETY/CRAVINGS), waist and blood pressure/pulse
// are DOCTOR_ONLY in the register until Ö-715 is answered; driv/lust
// (DRIVE_MOOD_CHANGE) is medical under A-01. Granting one of them is a
// change to the register entry, not to this file or to a route.

/**
 * The register question each measurement type is a reading of. Height and
 * hip have none, so no permission can name them and the coach never gets
 * them.
 */
export const MEASUREMENT_TYPE_QUESTION: Readonly<
  Record<MeasurementType, string | null>
> = {
  weight: "WEIGHT_KG",
  waist: "WAIST_CM",
  blood_pressure: "BLOOD_PRESSURE_PULSE",
  pulse: "BLOOD_PRESSURE_PULSE",
  height: null,
  hip: null,
};

/** Whether the register explicitly lets the coach read this question. */
export function coachMayReadQuestion(questionId: string): boolean {
  return registerQuestion(questionId)?.visibility.coach === true;
}

/** The "Mående" series the coach reads, in the patient's order. */
export const COACH_WELLBEING_QUESTIONS: readonly WellbeingSeriesQuestionId[] =
  WELLBEING_SERIES_QUESTIONS.map((q) => q.questionId).filter((id) =>
    coachMayReadQuestion(id),
  );

/** The measurement types the coach reads. */
export const COACH_MEASUREMENT_TYPES: readonly MeasurementType[] = (
  Object.keys(MEASUREMENT_TYPE_QUESTION) as MeasurementType[]
).filter((type) => {
  const questionId = MEASUREMENT_TYPE_QUESTION[type];
  return questionId !== null && coachMayReadQuestion(questionId);
});

/**
 * A-01's "8–12 veckor": the coach's graphs show the last twelve weeks. The
 * server applies it, so an older answer is not in her payload at all.
 */
export const COACH_GRAPH_WINDOW_WEEKS = 12;

/**
 * GET employee-api/v1/patients/:patientId/coach-graphs — the coach's graphs,
 * read from the same series the patient and the doctor read: "Mående" from
 * WellbeingSeriesService, the measurements from MeasurementService, drawn
 * with the shared groups and scales (measurement-chart.ts).
 */
export interface CoachGraphsView {
  windowWeeks: number;
  /** Only COACH_WELLBEING_QUESTIONS. */
  wellbeing: WellbeingSeriesView;
  /**
   * Only COACH_MEASUREMENT_TYPES, current values only, without the doctor's
   * assessment or where it was entered (a surface such as F-07 would say
   * more than the value does).
   */
  measurements: Measurement[];
}
